import { useEffect, useState } from 'react'
import { Gift, Loader2, Lock, Plus, ShoppingBag, Trash2, Unlock } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import type { Wishlist, WishlistKind } from '@/lib/wishlist'
import { useLanguage } from '@/context/LanguageContext'
import type { TranslationKey } from '@/i18n/homeTranslations'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface WishlistBoardManagerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  userId: string
  boards: Wishlist[]
  onChange: (boards: Wishlist[]) => void
}

const inputClass =
  'w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-900 outline-none transition focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10'

const boardKind = (board: Wishlist): WishlistKind =>
  board.kind === 'guide' ? 'guide' : 'wishlist'

export default function WishlistBoardManager({
  open,
  onOpenChange,
  userId,
  boards,
  onChange,
}: WishlistBoardManagerProps) {
  const { t } = useLanguage()
  const [newTitle, setNewTitle] = useState('')
  const [selectedKind, setSelectedKind] = useState<WishlistKind>('wishlist')
  const [busy, setBusy] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setNewTitle('')
      setSelectedKind('wishlist')
      setConfirmDeleteId(null)
      setError(null)
    }
  }, [open])

  const kindIcon = (kind: WishlistKind, className: string) =>
    kind === 'guide' ? (
      <ShoppingBag className={className} aria-label={t('boards.icon.guide' as TranslationKey)} />
    ) : (
      <Gift className={className} aria-label={t('boards.icon.wishlist' as TranslationKey)} />
    )

  const rename = async (id: string, title: string) => {
    const next = title.trim()
    if (!next) return
    const { error: err } = await supabase.from('wishlists').update({ title: next }).eq('id', id)
    if (err) {
      setError(err.message)
      return
    }
    onChange(boards.map((b) => (b.id === id ? { ...b, title: next } : b)))
    supabase.functions.invoke('notify-board-followers', { body: { wishlist_id: id } }).catch((e) => console.error('notify-board-followers failed', e))
  }

  const create = async () => {
    const title = newTitle.trim()
    if (!title || busy) return
    setBusy(true)
    setError(null)
    const position = boards.length ? Math.max(...boards.map((b) => b.position)) + 1 : 0
    const { data, error: err } = await supabase
      .from('wishlists')
      .insert({ user_id: userId, title, position, kind: selectedKind })
      .select()
      .single()
    setBusy(false)
    if (err) {
      setError(err.message)
      return
    }
    onChange([...boards, data as unknown as Wishlist])
    setNewTitle('')
    setSelectedKind('wishlist')
  }

  const togglePrivate = async (id: string, next: boolean) => {
    if (busy) return
    setBusy(true)
    setError(null)
    const { error: err } = await supabase.from('wishlists').update({ is_private: next }).eq('id', id)
    setBusy(false)
    if (err) {
      setError(err.message)
      return
    }
    onChange(boards.map((b) => (b.id === id ? { ...b, is_private: next } : b)))
    supabase.functions.invoke('notify-board-followers', { body: { wishlist_id: id } }).catch((e) => console.error('notify-board-followers failed', e))
  }

  const remove = async (id: string) => {
    if (boards.length <= 1 || busy) return
    setBusy(true)
    setError(null)
    supabase.functions.invoke('notify-board-followers', { body: { wishlist_id: id } }).catch((e) => console.error('notify-board-followers failed', e))
    const { error: err } = await supabase.from('wishlists').delete().eq('id', id)
    setBusy(false)
    if (err) {
      setError(err.message)
      return
    }
    onChange(boards.filter((b) => b.id !== id))
    setConfirmDeleteId(null)
  }

  const kindCards: { kind: WishlistKind; labelKey: TranslationKey; descKey: TranslationKey }[] = [
    { kind: 'wishlist', labelKey: 'boards.kind.wishlist' as TranslationKey, descKey: 'boards.kind.wishlist.desc' as TranslationKey },
    { kind: 'guide', labelKey: 'boards.kind.guide' as TranslationKey, descKey: 'boards.kind.guide.desc' as TranslationKey },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md glass-strong" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold text-neutral-900">
            {t('boards.manager.title' as TranslationKey)}
          </DialogTitle>
        </DialogHeader>

        <ul className="space-y-2">
          {boards.map((board) => {
            const kind = boardKind(board)
            return (
              <li
                key={board.id}
                className="flex items-center gap-2 rounded-2xl border border-border glass px-3 py-2.5"
              >
                <span
                  title={kind === 'guide' ? t('boards.icon.guide' as TranslationKey) : t('boards.icon.wishlist' as TranslationKey)}
                  aria-label={kind === 'guide' ? t('boards.icon.guide' as TranslationKey) : t('boards.icon.wishlist' as TranslationKey)}
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${kind === 'guide' ? 'bg-pink-100 text-pink-600' : 'bg-neutral-100 text-neutral-600'}`}
                >
                  {kindIcon(kind, 'h-3.5 w-3.5')}
                </span>
                <input
                  defaultValue={board.title}
                  onBlur={(e) => {
                    if (e.target.value.trim() && e.target.value.trim() !== board.title) {
                      rename(board.id, e.target.value)
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                  }}
                  className={`${inputClass} border-transparent px-1 py-1 font-semibold focus:border-neutral-900`}
                />
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => togglePrivate(board.id, !board.is_private)}
                    disabled={busy}
                    title={board.is_private ? t('boards.manager.privateHint' as TranslationKey) : t('boards.manager.publicHint' as TranslationKey)}
                    className={`flex h-8 w-8 items-center justify-center rounded-full transition disabled:opacity-50 ${board.is_private ? 'bg-amber-100 text-amber-600 hover:bg-amber-200' : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'}`}
                  >
                    {board.is_private ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
                  </button>
                  {confirmDeleteId === board.id ? (
                    <div className="flex shrink-0 items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => remove(board.id)}
                        disabled={busy}
                        className="rounded-full bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : t('boards.manager.confirmDelete' as TranslationKey)}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-semibold text-neutral-900 hover:bg-neutral-100 transition-colors"
                      >
                        {t('boards.manager.cancel' as TranslationKey)}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(board.id)}
                      disabled={boards.length <= 1}
                      title={
                        boards.length <= 1
                          ? t('boards.manager.minBoards' as TranslationKey)
                          : t('boards.manager.deleteTitle' as TranslationKey)
                      }
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-red-500 transition hover:bg-red-50 disabled:opacity-40"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </li>
            )
          })}
        </ul>

        <div className="mt-4 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {t('boards.manager.kind' as TranslationKey)}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {kindCards.map(({ kind, labelKey, descKey }) => {
              const selected = selectedKind === kind
              return (
                <button
                  key={kind}
                  type="button"
                  onClick={() => setSelectedKind(kind)}
                  aria-pressed={selected}
                  className={`flex flex-col items-start gap-1.5 rounded-2xl border p-3 text-left transition ${
                    selected
                      ? 'border-neutral-900 bg-neutral-900/5 ring-2 ring-neutral-900/10'
                      : 'border-border bg-white hover:border-neutral-300'
                  }`}
                >
                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-full ${
                      kind === 'guide' ? 'bg-pink-100 text-pink-600' : 'bg-neutral-100 text-neutral-600'
                    }`}
                  >
                    {kindIcon(kind, 'h-4 w-4')}
                  </span>
                  <span className="text-sm font-bold text-neutral-900">{t(labelKey)}</span>
                  <span className="text-xs leading-snug text-neutral-500">{t(descKey)}</span>
                </button>
              )
            })}
          </div>
        </div>

        <div className="mt-3 flex gap-2">
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') create()
            }}
            placeholder={t('boards.manager.namePlaceholder' as TranslationKey)}
            className={inputClass}
          />
          <button
            type="button"
            onClick={create}
            disabled={busy || !newTitle.trim()}
            className="inline-flex shrink-0 items-center gap-2 text-sm font-medium text-white bg-neutral-900 rounded-full px-5 py-2.5 hover:bg-neutral-800 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {t('boards.manager.create' as TranslationKey)}
          </button>
        </div>

        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      </DialogContent>
    </Dialog>
  )
}
